import React, {useCallback} from 'react';
import {List} from 'immutable';
import {modes} from 'components/GraphingInput/GraphingInput.react';

import {ResultIndicatorBar} from '../shared/ContentBlocks.react';

import {
  QuestionComponent,
  QuestionComponentProps
} from '../shared/QuestionComponent/QuestionComponent.react';

import GraphingQuestionInput from './GraphingQuestionInput/GraphingQuestionInput.react';

const GraphingQuestionComponent = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  customHeader,
  displayOrientation = 'default',
  guess,
  hasQuestionSetAccessPredicate,
  headingActions,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  postSubmissionScrollPointRef,
  showDifficulty,
  questionState
}: QuestionComponentProps) => {
  const customResultIndicator = (
    <>
      <ResultIndicatorBar
        guess={guess}
        additionalInfo={(() => {
          const submittedEntitiesCount = guess?.getIn(['content', 'entities'], List()).size;
          const expectedEntityCount = (activeQuestion.getValidResponse() || List()).size;
          const isSubmitted = !!guess?.getId();

          if (expectedEntityCount > 0 && submittedEntitiesCount === 0 && !isSubmitted) {
            return '';
          }

          if (expectedEntityCount > 0 && submittedEntitiesCount === 0 && isSubmitted) {
            return 'No answer submitted.';
          }

          if (submittedEntitiesCount > expectedEntityCount) {
            return 'Too many answers.';
          }

          if (submittedEntitiesCount < expectedEntityCount) {
            return 'Incomplete answer.';
          }
          return null;
        })()}
      />
      <GraphingQuestionInput
        isExplanation
        mode={modes.reviewing}
        storeName={activeQuestionStore.getName()}
        question={activeQuestion}
        guess={guess}
      />
    </>
  );

  // memoizing the input area wrapper to prevent re-render loop when GQI updates guess entities
  const GraphingQuestionInputAreaWrapper = useCallback(
    (props) => (
      <GraphingQuestionInput
        {...props}
        guess={guess}
        mode={questionState === 'PreGuess' ? modes.guessing : modes.reviewing}
      />
    ),
    [questionState, guess]
  );

  return (
    <QuestionComponent
      actions={actions}
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      activeSubject={activeSubject}
      customHeader={customHeader}
      customHOA={<></>}
      customResultIndicator={customResultIndicator}
      displayOrientation={displayOrientation}
      guess={guess}
      hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
      headingActions={headingActions}
      interactive={interactive}
      isMobile={isMobile}
      modifyTitle={modifyTitle}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      questionState={questionState}
      showDifficulty={showDifficulty}
      ActiveQuestionInputArea={GraphingQuestionInputAreaWrapper}
    />
  );
};

type GraphingQuestionTypeProps = Omit<QuestionComponentProps, 'questionState'>;

const GraphingQuestion = {
  PreGuess: (props: GraphingQuestionTypeProps) => (
    <GraphingQuestionComponent {...props} questionState='PreGuess' />
  ),
  PostGuess: (props: GraphingQuestionTypeProps) => (
    <GraphingQuestionComponent {...props} questionState='PostGuess' />
  )
};

export default GraphingQuestion;
